import { datadogRum } from '@datadog/browser-rum'
import { FC, ReactNode, useEffect, useRef, useState } from 'react'

import { getOAuthSubject as getOAuthSubjectUtil } from '~/notes/utils/cookies'

import { getFlagValue } from './getFlagValue'
import { isFlagEnabledForUser } from './isFlagEnabled'
import { FlagValues } from './types'
import { fetchGroupIds, loadGroupScopedFlags, unleashClient } from './unleash'

/**
 * React provider and hooks related to feature flags in the regard app.
 * Inspired by @unleash/proxy-client-react with additions
 * to include the legacy `window` and `localStorage` Regard flags.
 * See https://github.com/Unleash/proxy-client-react/
 */

type ProviderState = {
  flagsError: null | Error
  flagsReady: boolean
}

/**
 * Regard feature flags are pulled from multiple places and fallback in the following order:
 *   1. Unleash
 *   2. user-config.js (user_config)
 *   3. Window
 *   4. LocalStorage
 * Note: The higher number takes precedence. ie. LocalStorage overrides Window, &c.
 */
export const FlagProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [providerState, setProviderState] = useState<ProviderState>({
    flagsReady: false,
    flagsError: null,
  })

  useEffect(() => {
    const errorCallback = (e: Error) => {
      datadogRum.addError(new Error('Unleash Error', { cause: e }))
      setProviderState((state) => ({
        ...state,
        flagsError: state.flagsError || e,
      }))
    }

    const clearErrorCallback = () => {
      setProviderState((state) => ({ ...state, flagsError: null }))
    }

    let timeout = 0
    let unleashReady = false
    let groupFlagsReady = false

    const markReady = () => {
      if (!unleashReady || !groupFlagsReady) return
      // wait for flags to resolve after useFlag gets the same event
      timeout = window.setTimeout(() => {
        setProviderState((state) => ({ ...state, flagsReady: true }))
      }, 0)

      // This flag can be surgically set in Unleash to force 100% session replay recording.
      // Also forced for Moose-authorized users so every Moose session is fully recorded.
      if (
        getFlagValue('MOOSE_ENABLED') ||
        unleashClient.isEnabled('frontend_force_session_replay_recording')
      ) {
        datadogRum.startSessionReplayRecording({ force: true })
      }
    }

    const readyCallback = () => {
      unleashReady = true
      markReady()
    }

    unleashClient.on('ready', readyCallback)
    unleashClient.on('error', errorCallback)
    unleashClient.on('recovered', clearErrorCallback)

    const userId = getOAuthSubjectUtil()
    unleashClient.setContextField('userId', userId)
    // Fetch the real group IDs from the live API before starting the client so the
    // proxy server evaluates groupId IN/NOT_IN constraints against the actual value.
    fetchGroupIds().then((groupIds) => {
      unleashClient.setContextField('groupId', groupIds[0] ?? '')
      unleashClient.start()
      return loadGroupScopedFlags(userId, groupIds)
    }).finally(() => {
      groupFlagsReady = true
      markReady()
    })

    return () => {
      unleashClient.off('error', errorCallback)
      unleashClient.off('ready', readyCallback)
      unleashClient.off('recovered', clearErrorCallback)
      unleashClient.stop()
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [])

  // Since we only support a single unleash client, we don't need a true "provider"
  // and can just return the children.
  if (providerState.flagsReady || providerState.flagsError) {
    return children
  }

  // return a blank white screen while flags are loading
  // prevents a purple loading screen flash
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'white',
        color: '#666',
        fontSize: '16px',
      }}
    />
  )
}

export const useUserFlag = (flagName: keyof Flags) => {
  // Don't actually need to pull the context to get the client since we only
  // use one for the entire app.
  // const { flagsReady, flagsError, unleashClient } = useContext(FlagContext);
  const [flag, setFlag] = useState(unleashClient.isEnabled(flagName))
  const flagRef = useRef<typeof flag>(undefined)
  flagRef.current = flag

  useEffect(() => {
    if (!unleashClient)
      throw new Error('useFlag must be used within a FlagProvider')

    const updateHandler = () => {
      const enabled = isFlagEnabledForUser(flagName, {}, unleashClient)
      if (enabled !== flagRef.current) {
        flagRef.current = enabled
        setFlag(!!enabled)
      }
    }

    unleashClient.on('update', updateHandler)
    return () => {
      unleashClient.off('update', updateHandler)
    }
  }, [flagName])

  return flag
}

export const useValueFlag = (flagName: keyof Flags) => {
  // Don't actually need to pull the context to get the client since we only
  // use one for the entire app.
  // const { flagsReady, flagsError, unleashClient } = useContext(FlagContext);
  const [flag, setFlag] = useState<FlagValues>(
    !!unleashClient.isEnabled(flagName),
  )
  const flagRef = useRef<FlagValues>(undefined)
  flagRef.current = flag

  useEffect(() => {
    if (!unleashClient)
      throw new Error('useFlag must be used within a FlagProvider')

    const updateHandler = () => {
      const value = getFlagValue(flagName, {}, unleashClient)
      if (value !== flagRef.current) {
        flagRef.current = value
        setFlag(value)
      }
    }

    unleashClient.on('update', updateHandler)
    return () => {
      unleashClient.off('update', updateHandler)
    }
  }, [flagName])

  return flag
}
